/**
 * The tax-iceberg page's state lives in its URL so any view can be bookmarked, shared and
 * indexed: the gross salary is a path segment and the display parameters that differ from the
 * default are query parameters. Pure functions, used by the page (both directions), the route,
 * the guard and the Head.
 *
 * Personal circumstances are deliberately NOT all part of the URL. Church membership and trade-union
 * fees are special-category-adjacent (GDPR Art. 9): they live in the page's local React state for
 * the current view only, so decodeTaxIcebergState ignores them and encodeTaxIcebergUrl never writes
 * them. Everything else still round-trips so a shared or bookmarked link reopens the same view.
 *
 * The path is translated per locale, keyword-first (Ahrefs, Finland: "veroprosenttilaskuri"
 * 20k/mo, "bruttopalkka" 14k/mo; sv "bruttolön"): 
 *   en  /en/tax-iceberg/4000-gross-income        /en/tax-iceberg/48000-gross-income-per-year
 *   fi  /fi/veroprosenttilaskuri/4000-bruttopalkka  /fi/veroprosenttilaskuri/48000-bruttopalkka-vuodessa
 *   sv  /sv/loneraknare/4000-bruttolon              /sv/loneraknare/48000-bruttolon-per-ar
 * English keeps the "iceberg" name because that audience discovers and links to it as a
 * data-visualisation piece. Same pattern as utils/routing/summer-jobs-resource-paths.ts.
 */

import { FINNISH_MUNICIPAL_TAX_RATES_2026 } from './finnish-municipal-tax-rates-2026';
import type { EmployerSize } from './calculate-employer-cost';

export type Period = 'month' | 'year';
export type TaxIcebergLocale = 'en' | 'fi' | 'sv';

/** The page's path segment per locale (no locale prefix). */
export const TAX_ICEBERG_PATHS: Record<TaxIcebergLocale, string> = {
  en: 'tax-iceberg',
  fi: 'veroprosenttilaskuri',
  sv: 'loneraknare',
};
/** The legacy segment every locale used before the translation; redirected by the guard. */
export const TAX_ICEBERG_LEGACY_SEGMENT = 'tax-iceberg';

/** Which figure the URL's amount is: the gross salary, or a target net ("I want EUR 3,000 in
 *  hand": the page solves the gross that leaves it). */
export type SalaryMode = 'gross' | 'net';

const SLUG_WORDS: Record<TaxIcebergLocale, { gross: string; net: string; year: string }> = {
  en: { gross: 'gross-income', net: 'net-income', year: '-per-year' },
  fi: { gross: 'bruttopalkka', net: 'nettopalkka', year: '-vuodessa' },
  sv: { gross: 'bruttolon', net: 'nettolon', year: '-per-ar' },
};

export function asTaxIcebergLocale(locale: string | undefined): TaxIcebergLocale {
  return locale === 'fi' || locale === 'sv' ? locale : 'en';
}

/** `/fi/veroprosenttilaskuri` etc. */
export function taxIcebergBasePath(locale: string | undefined): string {
  const l = asTaxIcebergLocale(locale);
  return `/${l}/${TAX_ICEBERG_PATHS[l]}`;
}

/** Path without the locale prefix, for components that add it themselves (Link, localizeRoute). */
export function taxIcebergLogicalPath(locale: string | undefined, gross?: number, period: Period = 'month', mode: SalaryMode = 'gross'): string {
  const l = asTaxIcebergLocale(locale);
  return gross ? `/${TAX_ICEBERG_PATHS[l]}/${buildTaxIcebergSlug(gross, period, l, mode)}` : `/${TAX_ICEBERG_PATHS[l]}`;
}

export interface TaxIcebergUrlState {
  /** The amount in the URL: the gross salary, or the target net when mode is `net`. */
  gross: number;
  period: Period;
  mode: SalaryMode;
  /** Tax card check (verokortti): base withholding rate %, annual income limit EUR, extra rate %. */
  taxCardRate: number | undefined;
  taxCardLimit: number | undefined;
  taxCardExtraRate: number | undefined;
  municipality: string;
  employerSize: EmployerSize | undefined;
  includeVat: boolean;
  churchMember: boolean;
  age: number | undefined;
  dependentChildren: number;
  isSoleGuardian: boolean;
  unionFees: number;
  commuting: number;
  dividends: number;
  capitalGains: number;
  unlistedDividends: number;
  unlistedNetAssets: number;
  detailedLayers: boolean;
  compare: number[];
}

export const DEFAULT_TAX_ICEBERG_STATE: TaxIcebergUrlState = {
  gross: 4000,
  period: 'month',
  mode: 'gross',
  taxCardRate: undefined,
  taxCardLimit: undefined,
  taxCardExtraRate: undefined,
  municipality: '',
  employerSize: undefined,
  includeVat: false,
  churchMember: false,
  age: undefined,
  dependentChildren: 0,
  isSoleGuardian: false,
  unionFees: 0,
  commuting: 0,
  dividends: 0,
  capitalGains: 0,
  unlistedDividends: 0,
  unlistedNetAssets: 0,
  detailedLayers: false,
  compare: [],
};

/** Monthly salaries linked from the page so the long tail has crawl paths. */
export const POPULAR_MONTHLY_SALARIES = [2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000, 7000, 8000, 10000];
/** Target nets with their own indexable pages ("paljonko brutto että nettona 3000"). */
export const POPULAR_MONTHLY_NETS = [2000, 2500, 3000, 3500, 4000, 5000];

/** Whether a parsed salary slug is one of the popular pages above (the ones in sitemap-base.xml).
 *  Any other amount is reachable (links pass the visitor's own salary) but canonicalises to the
 *  base calculator, so links across thousands of amounts consolidate on one page. */
export function isPopularTaxIcebergSlug(parsed: { gross: number; period: Period; mode: SalaryMode } | null): boolean {
  if (!parsed || parsed.period !== 'month') return false;
  return (parsed.mode === 'net' ? POPULAR_MONTHLY_NETS : POPULAR_MONTHLY_SALARIES).includes(parsed.gross);
}

/** `4000-gross-income`, `48000-gross-income-per-year`, `3000-net-income` (and the fi/sv words). */
export function buildTaxIcebergSlug(amount: number, period: Period, locale: string | undefined = 'en', mode: SalaryMode = 'gross'): string {
  const w = SLUG_WORDS[asTaxIcebergLocale(locale)];
  return `${Math.max(0, Math.round(amount))}-${mode === 'net' ? w.net : w.gross}${period === 'year' ? w.year : ''}`;
}

/** Parses a salary slug in any locale's form; reports which locale's form it was. `gross` is
 *  the amount in the slug: the gross salary, or the target net when `mode` is `net`. */
export function parseTaxIcebergSlug(slug: string | undefined): { gross: number; period: Period; mode: SalaryMode; slugLocale: TaxIcebergLocale } | null {
  if (!slug) return null;
  for (const l of Object.keys(SLUG_WORDS) as TaxIcebergLocale[]) {
    const w = SLUG_WORDS[l];
    const m = new RegExp(`^(\\d{1,7})-(${w.gross}|${w.net})(${w.year})?$`).exec(slug);
    if (!m) continue;
    const gross = Number(m[1]);
    if (!(gross > 0)) return null;
    return { gross, period: m[3] ? 'year' : 'month', mode: m[2] === w.net ? 'net' : 'gross', slugLocale: l };
  }
  return null;
}

type Search = Record<string, string | string[] | undefined>;

function first(v: string | string[] | undefined): string | undefined {
  return Array.isArray(v) ? v[0] : v;
}
function nonNegInt(v: string | undefined, max = 100_000_000): number | undefined {
  if (v === undefined || v === '') return undefined;
  const x = Number(v);
  if (!Number.isFinite(x) || x < 0) return undefined;
  return Math.min(max, Math.round(x));
}
function flag(v: string | undefined): boolean {
  return v === '1' || v === 'true' || v === 'yes';
}

/** Read the state from the route's slug and the query string, falling back to defaults for
 * anything missing or malformed. Never throws: a bad parameter is simply ignored. */
export function decodeTaxIcebergState(slug: string | undefined, search: Search): TaxIcebergUrlState {
  const d = DEFAULT_TAX_ICEBERG_STATE;
  const parsed = parseTaxIcebergSlug(slug);
  const municipality = first(search.municipality) ?? '';
  const employer = first(search.employer);
  const compare = (first(search.compare) ?? '')
    .split(',')
    .map((s) => nonNegInt(s))
    .filter((x): x is number => x !== undefined && x > 0)
    .slice(0, 4);
  const pct = (v: string | undefined) => {
    if (v === undefined || v === '') return undefined;
    const x = Number(v.replace(',', '.'));
    return Number.isFinite(x) && x >= 0 && x <= 100 ? Math.round(x * 100) / 100 : undefined;
  };
  return {
    gross: parsed?.gross ?? d.gross,
    period: parsed?.period ?? d.period,
    mode: parsed?.mode ?? d.mode,
    taxCardRate: pct(first(search.card)),
    taxCardLimit: nonNegInt(first(search.limit)),
    taxCardExtraRate: pct(first(search.extra)),
    municipality: Object.prototype.hasOwnProperty.call(FINNISH_MUNICIPAL_TAX_RATES_2026, municipality) ? municipality : '',
    employerSize: employer === 'small' || employer === 'large' ? employer : undefined,
    includeVat: flag(first(search.vat)),
    // Session-only: never read from the query string (see the file header).
    churchMember: d.churchMember,
    age: (() => {
      const a = nonNegInt(first(search.age), 120);
      return a !== undefined && a > 0 ? a : undefined;
    })(),
    dependentChildren: nonNegInt(first(search.children), 20) ?? 0,
    isSoleGuardian: flag(first(search.sole)),
    unionFees: d.unionFees,
    commuting: nonNegInt(first(search.commuting)) ?? 0,
    dividends: nonNegInt(first(search.dividends)) ?? 0,
    capitalGains: nonNegInt(first(search.gains)) ?? 0,
    unlistedDividends: nonNegInt(first(search.own)) ?? 0,
    unlistedNetAssets: nonNegInt(first(search.assets)) ?? 0,
    detailedLayers: first(search.strata) === 'every',
    compare,
  };
}

/** The URL for a state: the base page when everything is default, else the salary slug plus
 * only the non-default parameters. */
export function encodeTaxIcebergUrl(state: TaxIcebergUrlState, locale: string): string {
  const d = DEFAULT_TAX_ICEBERG_STATE;
  const q = new URLSearchParams();
  if (state.municipality) q.set('municipality', state.municipality);
  if (state.employerSize) q.set('employer', state.employerSize);
  if (state.includeVat) q.set('vat', '1');
  // Church membership and union fees are intentionally not serialised — see the file header.
  if (state.age !== undefined) q.set('age', String(state.age));
  if (state.dependentChildren > 0) q.set('children', String(state.dependentChildren));
  if (state.dependentChildren > 0 && state.isSoleGuardian) q.set('sole', '1');
  if (state.commuting > 0) q.set('commuting', String(state.commuting));
  if (state.dividends > 0) q.set('dividends', String(state.dividends));
  if (state.capitalGains > 0) q.set('gains', String(state.capitalGains));
  if (state.unlistedDividends > 0) q.set('own', String(state.unlistedDividends));
  if (state.unlistedNetAssets > 0) q.set('assets', String(state.unlistedNetAssets));
  if (state.taxCardRate !== undefined) q.set('card', String(state.taxCardRate));
  if (state.taxCardRate !== undefined && state.taxCardLimit !== undefined && state.taxCardLimit > 0) q.set('limit', String(state.taxCardLimit));
  if (state.taxCardRate !== undefined && state.taxCardExtraRate !== undefined) q.set('extra', String(state.taxCardExtraRate));
  if (state.detailedLayers) q.set('strata', 'every');
  if (state.compare.length) q.set('compare', state.compare.join(','));
  const query = q.toString();
  const isDefaultSalary = state.gross === d.gross && state.period === d.period && state.mode === d.mode;
  const base = taxIcebergBasePath(locale);
  const path = isDefaultSalary && !query ? base : `${base}/${buildTaxIcebergSlug(state.gross, state.period, locale, state.mode)}`;
  return query ? `${path}?${query}` : path;
}
